import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import AddressAutocomplete from "app/frontend/components/AddressAutocomplete";
import { useContactSubmissionProjectParams } from "app/frontend/components/contact/useContactSubmissionProjectParams";
import { useIsEligibilityReadOnly } from "app/frontend/components/contact/useIsEligibilityReadOnly";
import BaseEligibilityAccordion from "app/frontend/components/eligibility/accordions/BaseEligibilityAccordion";
import { IEligibilityProps } from "app/frontend/components/eligibility/accordions/types";
import { EEligibilityStep } from "app/frontend/components/eligibility/constants";
import { useOnContinue } from "app/frontend/components/eligibility/hooks/useOnContinue";
import { IS_PRODUCTION } from "app/frontend/constants";
import { FormContext } from "app/frontend/contexts/FormContext";
import { useProjectParams } from "app/frontend/hooks/usePropsParams";
import { removeNonDigits } from "app/frontend/utils/phone";
import { useCallback, useMemo } from "react";
import { useInertiaForm } from "use-inertia-form";

const STATE = "NY";
const DEFAULT_VALUES = IS_PRODUCTION
    ? {
          street: "",
          city: "",
          state: STATE,
          zip: "",
          latitude: 0,
          longitude: 0,
          isAddressUnknown: false,
      }
    : {
          street: "4000 Barnes Avenue",
          city: "Bronx",
          state: STATE,
          zip: "10466",
          latitude: 40.8876581,
          longitude: -73.8569788,
          isAddressUnknown: false,
      };

/**
 * Inertia's useForm hook expects the type of data to be passed as type argument.
 * That data should be of type type FormDataType = Record<string, FormDataConvertible>;
 * However, inertia does not export either FormDataType or FormDataConvertible,
 * so we extend Record<string, any> instead.
 */
interface IFormData extends Record<string, any> {
    latitude: number;
    longitude: number;
    street: string;
    city: string;
    state: string;
    zip: string;
    zip_extension?: string;
    google_place_id?: string;
    isAddressUnknown: boolean;
}

const UNKNOWN = "UNKNOWN";

const ProjectLocationAccordion: React.FC<IEligibilityProps> = () => {
    const projectParams = useProjectParams();

    const contactParams = useContactSubmissionProjectParams();
    const readOnly = useIsEligibilityReadOnly();

    const defaultValues = useMemo(
        () => ({
            ...DEFAULT_VALUES,
            street: contactParams?.project?.street || DEFAULT_VALUES.street,
            zip: contactParams?.project?.zip || DEFAULT_VALUES.zip,
            city: contactParams?.project?.city || DEFAULT_VALUES.city,
            latitude: contactParams?.project?.latitude || DEFAULT_VALUES.latitude,
            longitude: contactParams?.project?.longitude || DEFAULT_VALUES.longitude,
        }),
        [
            contactParams?.project?.city,
            contactParams?.project?.latitude,
            contactParams?.project?.longitude,
            contactParams?.project?.street,
            contactParams?.project?.zip,
        ],
    );

    const form = useInertiaForm<IFormData>(defaultValues);

    const onContinue = useOnContinue("post");

    const getNewProjectParams = useCallback(() => {
        const newProjectParams = form.data.isAddressUnknown
            ? {
                  plugs: [],
                  street: UNKNOWN,
                  city: UNKNOWN,
                  zip: UNKNOWN,
                  latitude: 0,
                  longitude: 0,
                  google_place_id: UNKNOWN,
                  address: UNKNOWN,
              }
            : {
                  plugs: [],
                  street: form.data.street,
                  city: form.data.city,
                  zip: form.data.zip,
                  latitude: form.data.latitude,
                  longitude: form.data.longitude,
                  state: "NY",
                  google_place_id: form.data.google_place_id,
                  address: `${form.data.street} NY, ${form.data.zip}`,
              };
        return newProjectParams;
    }, [
        form.data.city,
        form.data.google_place_id,
        form.data.isAddressUnknown,
        form.data.latitude,
        form.data.longitude,
        form.data.street,
        form.data.zip,
    ]);

    const handleContinue = useCallback(() => {
        const newProjectParams = getNewProjectParams();

        onContinue({ newProjectParams });
    }, [getNewProjectParams, onContinue]);

    const header = useMemo(() => {
        if (projectParams.address) {
            if (projectParams.address === UNKNOWN) {
                return "Project Location - Unknown";
            } else {
                return `Project Location - ${removeNonDigits(projectParams.address.split(",").last())}`;
            }
        } else {
            return "Project Location";
        }
    }, [projectParams.address]);

    return (
        <FormContext.Provider value={form}>
            <BaseEligibilityAccordion
                header={header}
                h2="Where is your project located?"
                h4="This helps us learn more about your project."
                onContinue={handleContinue}
                step={EEligibilityStep.LOCATION}
                isContinueDisabled={(!form.data.street || !form.data.zip) && !form.data.isAddressUnknown}
            >
                <Grid container spacing={0} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 0, md: 1, lg: 2 }}>&nbsp;</Grid>
                    <Grid size={{ xs: 12, md: 10, lg: 8 }}>
                        <Grid container spacing={2} paddingX={{ sm: 10, md: 2 }}>
                            <Grid size={12}>
                                <AddressAutocomplete disabled={form.data.isAddressUnknown} />
                            </Grid>
                            <Grid size={12}>
                                <FormControlLabel
                                    sx={{ fontSize: "14px", fontWeight: 400 }}
                                    label={"I don't know yet"}
                                    control={
                                        <Checkbox
                                            disabled={readOnly}
                                            name={"isAddressUnknown"}
                                            id={"isAddressUnknown"}
                                            checked={form.data.isAddressUnknown}
                                            onChange={(e) => form.setData("isAddressUnknown", e.currentTarget.checked)}
                                        />
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 0, md: 1, lg: 2 }}>&nbsp;</Grid>
                </Grid>
            </BaseEligibilityAccordion>
        </FormContext.Provider>
    );
};

export default ProjectLocationAccordion;
